// Prerequisite presentation logic — deterministic, readable copy for the
// stage-2 "prerequisites" bucket (§4: blockers like a request type the form
// assumes). The AI emits { kind, value, note }; phrasing here is COMPOSED BY
// CODE from kind + value so the wording is consistent regardless of what the
// model wrote — the AI's note rides along as secondary detail.
//
// Where live post-options data is available, a prerequisite is also screened
// against the site's actual ID→name maps (request types, equipment types, …)
// so the copy can say "already exists — no action needed" vs "create it
// first" instead of hedging. Kinds with no live map stay honest: "if it
// doesn't exist yet".
//
// The where-to-fix hints were verified against the FMX help center
// (help.gofmx.com) on 2026-07-30.
//
// Pure module: no React, no Supabase, no fetch — node-testable.

import { normalizeFieldName } from './dedupe.js';

// Prompt contract (migration 033): kind is one of
// requestType | equipmentType | resourceType | userType | building | other.
// scopingKeys: which post-options systemFields carry this kind's live
// ID→name map (null = no live map through /custom-fields/post-options).
export const PREREQ_KINDS = {
  requestType: {
    label: 'Request type',
    noun: 'request type',
    scopingKeys: ['requestTypeIDs'],
    hint: 'In FMX: Admin Settings → Request types (pick the matching module).',
  },
  equipmentType: {
    label: 'Equipment type',
    noun: 'equipment type',
    scopingKeys: ['equipmentTypeIDs'],
    hint: 'In FMX: Admin Settings → Equipment types.',
  },
  resourceType: {
    label: 'Resource type',
    noun: 'resource type',
    scopingKeys: ['resourceTypeIDs'],
    hint: 'In FMX: Admin Settings → Resource settings.',
  },
  userType: {
    label: 'User type',
    noun: 'user type',
    scopingKeys: ['userTypeIDs', 'userTypeRestrictionIDs'],
    hint: 'In FMX: Admin Settings → User types.',
  },
  building: {
    label: 'Building',
    noun: 'building',
    scopingKeys: null, // buildings have no map in /custom-fields/post-options
    hint: 'In FMX: Buildings → Add building.',
  },
  other: {
    label: 'Prerequisite',
    noun: null,
    scopingKeys: null,
    hint: null,
  },
};

function kindInfo(kind) {
  return PREREQ_KINDS[kind] || PREREQ_KINDS.other;
}

/**
 * Collect the live names available per prerequisite kind from the fetched
 * post-options: { requestType: ['Electrical', …], equipmentType: […], … }.
 * A kind is present only when at least one fieldset's post-options actually
 * carries its scoping map — absence means "we can't know", not "empty site".
 *
 * @param postOptionsByFieldset { [fieldset]: { systemFields: [{key, options}] } }
 */
export function liveNamesByPrereqKind(postOptionsByFieldset = {}) {
  const out = {};
  for (const po of Object.values(postOptionsByFieldset)) {
    for (const sf of po?.systemFields || []) {
      for (const [kind, info] of Object.entries(PREREQ_KINDS)) {
        if (!info.scopingKeys?.includes(sf.key) || !sf.options) continue;
        out[kind] = [...(out[kind] || []), ...Object.values(sf.options)];
      }
    }
  }
  return out;
}

/**
 * Screen one prerequisite against the live names for its kind.
 * Returns 'found' | 'missing' | 'unknown' plus the matched live name.
 * Comparison uses the same normalizer as the duplicate screen, so
 * "Chiller — Centrifugal" matches "Chiller Centrifugal".
 */
export function prereqStatus(item, liveNames) {
  const names = liveNames?.[item?.kind];
  if (!Array.isArray(names)) return { status: 'unknown', match: null };
  const norm = normalizeFieldName(item?.value);
  if (!norm) return { status: 'unknown', match: null };
  const match = names.find(n => normalizeFieldName(n) === norm) || null;
  return match ? { status: 'found', match } : { status: 'missing', match: null };
}

/**
 * The composed, human-readable copy for one prerequisite:
 *   { kindLabel, headline, detail, hint, status, match }
 * headline is action-first; detail carries the site-status sentence plus the
 * AI's note; hint is the verified where-to-fix line (null when not needed).
 */
export function composePrereq(item, liveNames = null) {
  const info = kindInfo(item?.kind);
  const { status, match } = prereqStatus(item, liveNames);
  const value = String(item?.value ?? '').trim();
  const note = (item?.note || '').trim() || null;

  if (!info.noun) {
    // 'other' — no safe noun to compose around; lead with the value itself.
    return {
      kindLabel: info.label, status, match,
      headline: value || 'Unresolved prerequisite',
      detail: note || 'The form assumes this exists in FMX — resolve it before pushing.',
      hint: null,
    };
  }

  if (status === 'found') {
    return {
      kindLabel: info.label, status, match,
      headline: `The ${info.noun} “${match}” already exists on this site`,
      detail: `No action needed — just confirm it’s the one the form means.${note ? ` (AI: ${note})` : ''}`,
      hint: null,
    };
  }
  if (status === 'missing') {
    return {
      kindLabel: info.label, status, match,
      headline: `Create the ${info.noun} “${value}” in FMX first`,
      detail: `The form expects it, and it isn’t on this site yet.${note ? ` (AI: ${note})` : ''}`,
      hint: info.hint,
    };
  }
  // unknown — no live map to check against; stay honest.
  return {
    kindLabel: info.label, status, match,
    headline: `Make sure the ${info.noun} “${value}” exists in FMX`,
    detail: `The form expects it — create it first if it isn’t on this site.${note ? ` (AI: ${note})` : ''}`,
    hint: info.hint,
  };
}
